import { db, safeDbOperation } from '../lib/db';
import { Category } from '../types/category';

export const addCategory = async (name: string): Promise<Category> => {
  return safeDbOperation(async () => {
    // Check if category already exists (case insensitive)
    const existingCategory = await db.categories
      .where('name')
      .equalsIgnoreCase(name)
      .first();

    if (existingCategory) {
      throw new Error('A category with this name already exists');
    }

    const newCategory: Category = {
      id: crypto.randomUUID(),
      name,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await db.categories.add(newCategory);
    return newCategory;
  });
};

export const updateCategory = async (id: string, name: string): Promise<Category> => {
  return safeDbOperation(async () => {
    const existingCategory = await db.categories.get(id);
    if (!existingCategory) {
      throw new Error('Category not found');
    }

    // Check if new name conflicts with existing category
    const nameConflict = await db.categories
      .where('name')
      .equalsIgnoreCase(name)
      .first();

    if (nameConflict && nameConflict.id !== id) {
      throw new Error('A category with this name already exists');
    }

    const updatedCategory: Category = {
      ...existingCategory,
      name,
      updatedAt: new Date(),
    };

    await db.categories.put(updatedCategory);
    return updatedCategory;
  });
};

export const deleteCategory = async (id: string): Promise<void> => {
  return safeDbOperation(async () => {
    // Check if category is in use
    const recipesUsingCategory = await db.recipes
      .where('category')
      .equals(id)
      .count();

    if (recipesUsingCategory > 0) {
      throw new Error('Cannot delete category that is being used by recipes');
    }

    await db.categories.delete(id);
  });
};

export const getCategories = async (): Promise<Category[]> => {
  return safeDbOperation(async () => {
    const categories = await db.categories.toArray();
    return categories.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  });
};

export const reorderCategory = async (id: string, newOrder: number): Promise<void> => {
  return safeDbOperation(async () => {
    const categories = await db.categories.orderBy('order').toArray();
    const category = categories.find(c => c.id === id);
    if (!category) throw new Error('Category not found');

    const oldOrder = category.order;
    if (newOrder === oldOrder) return;

    // Update orders of affected categories
    if (newOrder > oldOrder) {
      // Moving down: decrease order of categories between old and new position
      for (const cat of categories) {
        if (cat.order > oldOrder && cat.order <= newOrder) {
          await db.categories.update(cat.id, { order: cat.order - 1 });
        }
      }
    } else {
      // Moving up: increase order of categories between new and old position
      for (const cat of categories) {
        if (cat.order >= newOrder && cat.order < oldOrder) {
          await db.categories.update(cat.id, { order: cat.order + 1 });
        }
      }
    }

    // Update the moved category's order
    await db.categories.update(id, { order: newOrder });
  });
};